import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { useEffect, useState } from 'react';
import { Bold, Italic, Underline as UnderlineIcon, Heading2, Heading3, List, ListOrdered, Link as LinkIcon, Image as ImageIcon, Minus, Undo, Redo } from 'lucide-react';

const ToolbarBtn = ({ onClick, active, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`p-2 rounded transition-colors text-sm ${
      active
        ? 'bg-primary text-white'
        : 'text-foreground/60 hover:bg-muted hover:text-foreground'
    }`}
  >
    {children}
  </button>
);

const ArticleEditor = ({ value, onChange }) => {
  const [linkUrl, setLinkUrl] = useState('');
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [showImageInput, setShowImageInput] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: false, underline: false }),
      Link.configure({ openOnClick: false, HTMLAttributes: { class: 'text-primary underline hover:text-primary/80' } }),
      Image.configure({ HTMLAttributes: { class: 'max-w-full rounded-xl my-6' } }),
      Underline,
      Placeholder.configure({ placeholder: 'Inizia a scrivere...' }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  if (!editor) return null;

  const addLink = () => {
    if (linkUrl) {
      if (editor.state.selection.empty) {
        editor.chain().focus().insertContent(`<a href="${linkUrl}">${linkUrl}</a>`).run();
      } else {
        editor.chain().focus().setLink({ href: linkUrl }).run();
      }
      setLinkUrl('');
      setShowLinkInput(false);
    }
  };

  const addImage = () => {
    if (imageUrl) {
      editor.chain().focus().setImage({ src: imageUrl }).run();
      setImageUrl('');
      setShowImageInput(false);
    }
  };

  return (
    <div className="border border-input rounded-lg overflow-hidden" data-testid="article-editor">
      <div className="flex flex-wrap items-center gap-0.5 p-2 bg-muted border-b border-input">
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Grassetto">
          <Bold size={15} />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Corsivo">
          <Italic size={15} />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Sottolineato">
          <UnderlineIcon size={15} />
        </ToolbarBtn>
        <div className="w-px h-5 bg-border mx-1" />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Titolo H2">
          <Heading2 size={15} />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Titolo H3">
          <Heading3 size={15} />
        </ToolbarBtn>
        <div className="w-px h-5 bg-border mx-1" />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Lista puntata">
          <List size={15} />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Lista numerata">
          <ListOrdered size={15} />
        </ToolbarBtn>
        <div className="w-px h-5 bg-border mx-1" />
        <ToolbarBtn onClick={() => setShowLinkInput(!showLinkInput)} active={editor.isActive('link')} title="Inserisci link">
          <LinkIcon size={15} />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => setShowImageInput(!showImageInput)} active={false} title="Inserisci immagine (URL)">
          <ImageIcon size={15} />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} active={false} title="Separatore">
          <Minus size={15} />
        </ToolbarBtn>
        <div className="w-px h-5 bg-border mx-1" />
        <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} active={false} title="Annulla">
          <Undo size={15} />
        </ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} active={false} title="Ripeti">
          <Redo size={15} />
        </ToolbarBtn>
      </div>

      {showLinkInput && (
        <div className="flex gap-2 p-2 bg-muted/50 border-b border-input">
          <input
            type="url"
            value={linkUrl}
            onChange={e => setLinkUrl(e.target.value)}
            placeholder="https://..."
            className="flex-1 text-sm border border-input rounded px-2 py-1 bg-background focus:outline-none focus:ring-1 focus:ring-primary"
            onKeyDown={e => e.key === 'Enter' && addLink()}
          />
          <button type="button" onClick={addLink} className="text-xs bg-primary text-white px-3 py-1 rounded">Aggiungi</button>
          <button type="button" onClick={() => editor.chain().focus().unsetLink().run()} className="text-xs text-destructive underline">Rimuovi link</button>
        </div>
      )}

      {showImageInput && (
        <div className="flex gap-2 p-2 bg-muted/50 border-b border-input">
          <input
            type="url"
            value={imageUrl}
            onChange={e => setImageUrl(e.target.value)}
            placeholder="URL immagine (https://...)"
            className="flex-1 text-sm border border-input rounded px-2 py-1 bg-background focus:outline-none focus:ring-1 focus:ring-primary"
            onKeyDown={e => e.key === 'Enter' && addImage()}
          />
          <button type="button" onClick={addImage} className="text-xs bg-primary text-white px-3 py-1 rounded">Inserisci</button>
        </div>
      )}

      <EditorContent
        editor={editor}
        className="prose prose-sm max-w-none p-4 min-h-[300px] focus:outline-none"
      />
    </div>
  );
};

export default ArticleEditor;
